import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Table, Button, Form, InputGroup, Card, Spinner, Alert, Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../../axiosConfig";

function formatDateRo(val) {
  if (!val) return "";
  const s = String(val);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
}

export default function SesizariList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" = Toate, "0" = Deschise, "1" = Finalizate
  const [page, setPage] = useState(1);
  const limit = 20;

  const navigate = useNavigate();

  const fetchList = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        search: search.trim() || undefined,
        skip: (page - 1) * limit,
        limit: limit,
      };
      if (statusFilter !== "") {
        params.finalizat = Number(statusFilter);
      }
      const res = await api.get("/contor/sesizari", { params });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Eroare la preluarea sesizărilor.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchList, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, statusFilter]);

  const handleDelete = async (id) => {
    if (!window.confirm("Sigur doriți să ștergeți acest tichet de service?")) {
      return;
    }
    try {
      await api.delete(`/contor/sesizari/${id}`);
      toast.success("Tichetul a fost șters.");
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Eroare la ștergerea tichetului.");
    }
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1 text-dark fw-bold">Sesizări & Tichete Service</h2>
          <p className="text-muted mb-0">Urmărirea solicitărilor de intervenții, citirilor de contoare și reparațiilor</p>
        </div>
        <Button
          variant="dark"
          onClick={() => navigate("/contor/sesizari/new")}
          className="shadow-sm px-4 py-2"
        >
          + Adaugă Sesizare
        </Button>
      </div>

      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="p-3">
          <Form onSubmit={(e) => { e.preventDefault(); fetchList(); }}>
            <Row className="g-2">
              <Col xs={12} md={5}>
                <Form.Control
                  placeholder="Caută după nr. doc, defect, client sau serie echipament..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                />
              </Col>
              <Col xs={12} md={3}>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                >
                  <option value="">— Toate Stadiile —</option>
                  <option value="0">Deschise (În lucru)</option>
                  <option value="1">Finalizate (Rezolvate)</option>
                </Form.Select>
              </Col>
              <Col xs={12} md={2}>
                <Button variant="outline-secondary" type="submit" className="w-100">
                  Caută
                </Button>
              </Col>
              {(search || statusFilter) && (
                <Col xs={12} md={2}>
                  <Button
                    variant="outline-danger"
                    onClick={() => { setSearch(""); setStatusFilter(""); setPage(1); }}
                    className="w-100"
                  >
                    Resetează
                  </Button>
                </Col>
              )}
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          <Table responsive hover className="align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: "120px" }}>Document</th>
                <th>Dată</th>
                <th>Echipament Serie</th>
                <th>Tip Solicitare</th>
                <th>Nume Client</th>
                <th>Descriere Defect / Diagnostic</th>
                <th>Tehnician</th>
                <th>Stadiu</th>
                <th>Contoare</th>
                <th style={{ width: "180px" }} className="text-end">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-5">
                    <Spinner animation="border" className="me-2" />
                    Se încarcă...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-muted text-center py-5">
                    Nu s-a găsit nicio sesizare service.
                  </td>
                </tr>
              ) : (
                items.map((it) => (
                  <tr key={it.bxid}>
                    <td className="fw-bold">{it.bxdoc}</td>
                    <td>{formatDateRo(it.bxdata)}</td>
                    <td>
                      <div className="fw-semibold text-dark">{it.echipament_seria}</div>
                      {it.echipament_descr && <small className="text-muted">{it.echipament_descr}</small>}
                    </td>
                    <td>
                      <Badge bg="secondary" className="fw-normal">
                        {it.tip_eveniment_descr || it.tip_eveniment_cod}
                      </Badge>
                    </td>
                    <td>{it.bxnume_client || <span className="text-muted">-</span>}</td>
                    <td>
                      <div
                        style={{
                          maxWidth: "250px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={it.bxdescr}
                      >
                        {it.bxdescr || <span className="text-muted">-</span>}
                      </div>
                    </td>
                    <td>{it.tehnician_name || <span className="text-muted">-</span>}</td>
                    <td>
                      {it.bxfinalizat === 1 ? (
                        <Badge bg="success">Rezolvat</Badge>
                      ) : (
                        <Badge bg="warning" text="dark">Deschis</Badge>
                      )}
                      {it.facturabil === 1 && (
                        <Badge bg="info" className="text-dark ms-1">
                          Facturabil
                        </Badge>
                      )}
                    </td>
                    <td>
                      {(it.m1 || it.m2 || it.m3 || it.m4) ? (
                        <div className="small font-monospace" style={{ fontSize: "11px" }}>
                          {it.m1 > 0 && <span>M1:{it.m1} </span>}
                          {it.m2 > 0 && <span>M2:{it.m2} </span>}
                          {it.m3 > 0 && <span>M3:{it.m3} </span>}
                          {it.m4 > 0 && <span>M4:{it.m4} </span>}
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="text-end">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => navigate(`/contor/sesizari/edit/${it.bxid}`)}
                      >
                        Editează
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(it.bxid)}
                      >
                        Șterge
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {total > limit && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <span className="text-muted small">Total: {total} sesizări</span>
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Anterior
            </Button>
            <span className="align-self-center px-2">Pagina {page}</span>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={page * limit >= total}
              onClick={() => setPage(page + 1)}
            >
              Următor
            </Button>
          </div>
        </div>
      )}
    </Container>
  );
}
